/* eslint-disable max-lines */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import provideDataHook, {
  DataHookFunctionProps
} from '~/helpers/provideDataHook';
import styled from 'styled-components';
import {
  BackIcon,
  BodyText,
  Breadcrumbs,
  Button,
  mq,
  Spacer,
  TinyTitleBold,
  Title
} from '@kvdbil/components';
import { Redirect, useHistory, useLocation, useParams } from 'react-router';
import {
  isHydratedSelector,
  memberIdSelector,
  session as sessionSelector
} from '~/App/shared/selectors/sessionSelector';
import { log } from '~/helpers/bugsnagHelper';
import { getUsersTargetedAuctionGroupIds } from '~/helpers/targetedAuctions';
import Head from '~/App/shared/components/Head';
import { generateVehicleStructuredData } from '~/helpers/structuredData';
import Recombee from './components/Recombee';
import Section from '~/App/shared/components/Layout/Section';
import Social from './components/Social';
import BuyMethodBox from './components/BuyMethodBox';
import Gallery from './components/Gallery';
import Equipment from './components/Equipment';
import Summary from './components/Summary';
import StickyAuctionSummary from './components/StickyAuctionSummary';
import { getAuction, viewAuction } from '~/App/shared/actions/auction';
import { useInterval } from '~/App/shared/hooks/useIntervalHook';
import Auction, {
  AuctionNotFound,
  AuctionType
} from '~/App/shared/interfaces/Auction';
import { useTranslation } from '~/Locale';
import BiddingView from '~/App/views/ObjectPage/components/BiddingView';
import VehicleCardListBlock from '~/App/shared/components/VehicleCardListBlock';
import {
  getCanonicalPath,
  getIdFromSlug,
  getMetaFromAuction,
  getReducedPrice,
  isComingSoonAuction,
  prettySlug,
  processAuction,
  processAuctionStates
} from '~/helpers/auction';
import { getAuctionBids } from '~/helpers/orchestration/auction';
import { reportProductView } from '~/helpers/emarsys';
import { websocketBidsById } from '~/App/shared/selectors/websockets';
import { wonAuctions as wonAuctionsSelector } from '~/App/shared/selectors/wonAuctions';
import {
  biddingViewSetAllBids,
  closeBiddingView,
  openBiddingView
} from '~/App/shared/actions/objectViewDrawerActions';
import { addViewedAuction } from '~/App/shared/actions/recently-viewed-auctions';
import { setHeaderHiddenAction } from '~/App/shared/actions/headerActions';
import {
  allBids,
  storeObjectAuctionId as biddingViewAuctionIdSelector,
  isObjectViewDrawerOpen,
  isFetching as isFetchingStoreObjects,
  storeObjectById
} from '~/App/shared/selectors/storeObject';
import {
  equipmentExists,
  getBreadcrumbs,
  getIsEVorPHEV,
  getMobileBreadcrumb,
  hasLocationData,
  isHeavyEquipmentObject,
  navigateBasedOnPreviousPage
} from './helpers';
import BreadcrumbsDropdown from './components/BreadcrumbsDropdown';
import { useGA4ObjectEvent } from '~/App/shared/hooks/useGA4ObjectEvent';
import translateRoute from '~/App/shared/localization/translateRoute';
import {
  AllBrandAndFamilySlugsDocument,
  AllBrandAndFamilySlugsQuery,
  ContactSectionRecord
} from '~/config/generated/graphql';
import Contact from './components/Contact';
import { getCmsData } from '~/helpers/cmsDatoUtils';
import { CarAllBrandAndFamilySlugsData } from '~/App/shared/interfaces/store/CmsData';
import { ProductCardVariant } from '~/App/shared/components/ProductCard/types';
import Badge from './components/Badge';
import SimilarCarsBox from './components/SimilarCarsBox';
import { useIsDevice } from '~/App/shared/hooks/useIsDevice';
import useCanonicalUrl from '~/App/shared/hooks/useCanonicalUrl';
import { StickyOnScrollContainer } from '~/App/shared/components/StickyOnScrollContainer';
import {
  setContactButtonDefaultTriggerEnabled,
  setContactButtonIsHidden,
  setContactButtonPosition
} from '~/App/shared/actions/contactButton';
import { IncludeContent } from '~/App/shared/components/MediaQueryContainer';
import TrustPilotTop from '~/App/shared/components/TrustPilotTop';
import BrieflyAboutObject from './components/BrieflyAboutObject';
import HeavyEquipmentDescription from './components/HeavyEquipmentDescription';
import HeavyEquipmentTerms from './components/HeavyEquipmentTerms';
import ObjectLocation from '~/App/shared/components/ObjectLocation';
import { localizationSelector } from '~/App/shared/selectors/localization';
import { useObjectPageCMSData } from './hooks';
import { openGallery } from './views/GalleryPage/helpers';
import { unionBy } from '~/helpers/unionBy';
import { RouteComponentProps } from '~/App/shared/interfaces/routes';
import KvdHelperWrapper from '~/App/shared/components/KvdHelperWrapper';
import { getPathFromMainCategory } from '~/helpers/filterTranslation';
import { useFeatureFlagValue } from '~/App/components/FeatureFlags';
import { MajorFeatures } from './components/MajorFeatures/MajorFeatures';
import BidOrReserveDrawer from './components/BidOrReserveDrawer';
import { AuctionTest } from './components/AuctionTest/AuctionTest';
import HeavyEquipmentFacts from './components/HeavyEquipmentFacts';
import AuctionFacts from './components/Facts';
import { globalInterestRate } from '~/App/shared/selectors/finance';

const CONTENT_WIDTH = 72;

const Container = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background.light};
`;

const BreadcrumbsWrapper = styled.div`
  display: flex;
  padding: 0.25rem 0;
  align-items: center;

  ul {
    flex-wrap: wrap;
  }

  ${mq('tablet')} {
    & button {
      margin-right: 1.25rem;
    }
  }

  ${mq(null, 'tablet')} {
    justify-content: space-between;

    #breadcrumbs-regular {
      display: none;
    }
  }

  ${mq('tablet')} {
    #breadcrumbs-mobile {
      display: none;
    }
  }
`;

const AuctionDetails = styled.div`
  width: 100%;
`;

const SoonTestedExplanation = styled(BodyText)`
  padding: 1rem 0;
`;

const EquipmentSoonAvailable = styled(TinyTitleBold)`
  color: ${({ theme }) => theme.colors.error.mainAlt};
  text-align: center;
  margin-bottom: 1.5rem;
`;

const GalleryWrapper = styled.div`
  width: 100%;
`;

const SummaryContainer = styled.div`
  display: flex;
`;

const VehicleCardListBlockWrapper = styled.div`
  padding-bottom: 2rem;
`;

const BuyMethodContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 0;
  gap: 2rem;

  ${mq(null, 'tablet')} {
    flex-direction: column;
  }
`;

const ResponsiveSection = styled(Section)`
  ${mq(null, 'tablet')} {
    padding: 0;
  }
`;

const BadgeContainer = styled.div`
  margin-bottom: 2rem;
`;

interface Props extends RouteComponentProps {
  isError404?: boolean;
  redirectUri?: string;
}

export const AuctionHandler = ({
  isError404,
  redirectUri,
  staticContext
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{
    query: { action: string };
    prevPath: string;
  }>();
  const params = useParams<{ slug: string }>();
  const bids = useSelector(allBids);
  const session = useSelector(sessionSelector);
  const wonAuctions = useSelector(wonAuctionsSelector);
  const websocketBids = useSelector(websocketBidsById);
  const isFetching = useSelector(isFetchingStoreObjects);
  const isBiddingOpen = useSelector(isObjectViewDrawerOpen);
  const biddingViewAuctionId = useSelector(biddingViewAuctionIdSelector);
  const memberId = useSelector(memberIdSelector);
  const isHydrated = useSelector(isHydratedSelector);
  const { isMobile } = useIsDevice();
  const auctionId = getIdFromSlug(params?.slug);
  const rawAuction = useSelector(storeObjectById(auctionId));
  const { previousURL } = useSelector(localizationSelector);
  const isElectrification = useFeatureFlagValue('electrification');
  const interestRate = useSelector(globalInterestRate);

  const urlAction: string = location?.state?.query?.action;

  const closedAt = rawAuction?.closedAt;

  const auction = processAuction(t, {
    auction: rawAuction || ({} as Auction),
    websocketBids: unionBy([...Object.values(websocketBids), ...bids], 'id')
  });

  const { processObject: { objectType = 'CAR' } = {} } = auction;

  const isEVorPHEV = getIsEVorPHEV(auction.processObject);

  const handleBack = () => {
    navigateBasedOnPreviousPage(
      t,
      previousURL,
      history,
      getPathFromMainCategory(t, auction.processObject.vehicleType)
    );
  };

  const auctionStates = processAuctionStates({
    auction,
    session,
    wonAuctions
  });

  const reservationPriceReached = Boolean(
    auction?.activeAuction?.reservationPriceReached
  );
  const isComingSoon = isComingSoonAuction(auction);

  const { data: cmsData } = useObjectPageCMSData();

  const {
    contact: contactSection,
    fixedPriceController: isContactFixedPrice,
    auctionController: isContactAuction
  } = cmsData?.contactSection ?? {};

  const objectConditionCmsData = cmsData?.objectCondition ?? [];

  const showContactOnAuction = Boolean(
    (isContactAuction && !auctionStates?.isBuyNow) ||
    (auctionStates?.isClosed && !reservationPriceReached)
  );
  const showContactOnFixedPrice = Boolean(
    isContactFixedPrice && auctionStates?.isBuyNow
  );

  useEffect(() => {
    if (auctionId && memberId) {
      dispatch(getAuction(auctionId));
    }
  }, [memberId, auctionId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setHeaderHiddenAction(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (auctionId) {
      dispatch(viewAuction(auctionId));
    }
  }, [auctionId, dispatch]);

  const getAllBids = useCallback(
    (auctionId: string) => {
      getAuctionBids(auctionId)
        .then(response => {
          dispatch(biddingViewSetAllBids(response.bids));
        })
        .catch((error: unknown) => {
          log(new Error('Failed to fetch auction'), {
            error,
            severity: 'warning'
          });
        });
    },
    [dispatch]
  );

  /* Fetch the auction again on Focus to have up to date information (?) */
  const gotFocus = useCallback(() => {
    if (auctionId) {
      dispatch(getAuction(auctionId));
    }
  }, [auctionId, dispatch]);

  useEffect(() => {
    if (urlAction === 'budgivning') {
      const pId = setTimeout(() => {
        if (auctionId) {
          dispatch(openBiddingView(auctionId, AuctionType.BIDDING, 'default'));
        }
      }, 1000);

      return () => clearTimeout(pId);
    }
  }, [urlAction, auctionId, dispatch]);

  useInterval(() => {
    if (!closedAt && !isComingSoon && !auctionStates?.isBuyNow) {
      getAllBids(auctionId);
    }
  }, 45000);

  useEffect(() => {
    window.addEventListener('focus', gotFocus);
    return () => window.removeEventListener('focus', gotFocus);
  }, [gotFocus]);

  useEffect(() => {
    if (isHydrated && auctionId && !closedAt && !isComingSoon) {
      reportProductView(auctionId, memberId);
    }
  }, [isHydrated, auctionId, memberId, closedAt, isComingSoon]);

  useGA4ObjectEvent('view_object', auction);

  const currentAuctionId = getIdFromSlug(params.slug);
  const isOldAuction = rawAuction?.id !== currentAuctionId;
  const auctionHasProcessObject = rawAuction?.processObject;

  const targetedAuctionGroupId = rawAuction?.targetedAuctionGroupId;
  const token = session?.auth?.idToken;
  const usersTargetedAuctionIds = token
    ? getUsersTargetedAuctionGroupIds(token)
    : [];
  const isMyPagesPath = location.pathname.includes(t('ROUTES.MY_ACCOUNT'));
  const hasPermissionForTargetedAuction = usersTargetedAuctionIds.includes(
    rawAuction?.targetedAuctionGroupId ?? 0
  );

  const notEligibleForTargetedAuction =
    targetedAuctionGroupId &&
    (!isMyPagesPath || !hasPermissionForTargetedAuction);

  const baseUri =
    auction?.auctionType === AuctionType.BUY_NOW
      ? '/ROUTES.FIXED_PRICE'
      : '/ROUTES.AUCTIONS';

  const pathnameBasedOnAuctionType = translateRoute(
    t,
    `${baseUri}/${params?.slug}`
  );

  const hasEquipment = equipmentExists(auction?.processObject);

  const hasFactsDefault =
    (auction?.processObject?.facts?.default ?? []).length > 0;
  const hasFactsAdditional =
    (auction?.processObject?.facts?.additional ?? []).length > 0;

  const similarObjects = useMemo(
    () => (rawAuction?.similarAuctions ?? []).slice(0, 4),
    [rawAuction?.similarAuctions]
  );

  const hasFacts = hasFactsDefault || hasFactsAdditional;

  const media = auction?.processObject?.media ?? [];

  const specifications = Object.values(auction?.previewSpecifications);
  const { metaTitle, metaDescription } = getMetaFromAuction(t, auction);
  const canonicalUrl = useCanonicalUrl(getCanonicalPath(t, auction));
  const isHeavyEquipment = isHeavyEquipmentObject(auction?.processObject);

  const meta = [
    { name: 'description', content: metaDescription },
    { name: 'og:description', content: specifications },
    { name: 'og:title', content: auction?.previewTitle ?? '' },
    { name: 'og:image', content: media?.[0]?.uri ?? '' },
    { name: 'og:type', content: t('PAGE_META.AUCTION_OG_TYPE') },
    {
      name: 'og:url',
      content: canonicalUrl
    }
  ];

  // Generate structured data for vehicle
  let structuredData = null;
  if (rawAuction) {
    try {
      structuredData = generateVehicleStructuredData({
        t,
        auction,
        auctionStates,
        interestRate,
        canonicalUrl
      });
    } catch (error) {
      log(new Error('Failed to generate vehicle structured data'), {
        error,
        auctionId: auction?.id,
        severity: 'error'
      });
    }
  }

  const headProps = {
    title: metaTitle,
    structuredData: structuredData || undefined
  };

  // FIXME: we should never have hardcoded logic based of a value like this
  // Fix for this would be to only display grades if data for grades exists
  const showGrades = [
    'CAR',
    'LIGHT_TRUCK',
    'CAMPER',
    'CARAVAN',
    'TRAILER',
    'MC_MOPED',
    'MOTOR_HOME', // TODO: remove
    'LIGHT_TRAILER',
    'BOAT' // TODO: should be deprecated but still is not...
  ].includes(objectType);

  const facility = auction?.processObject?.locationInfo?.facility;

  const handleGalleryClick = (index: number) => {
    openGallery({
      index,
      history,
      params,
      t,
      imageType: 'PRESENTATION',
      sellMethod: auction.auctionType
    });
  };

  const { isReserved } = auction;

  const { isPriceReduced } = getReducedPrice(auction);

  const renderedBadge = useMemo(() => {
    if (isComingSoon) {
      return <Badge>{t('Coming soon')}</Badge>;
    }

    if (auction?.isSoldByBuyNow || Boolean(auction?.soldFor)) {
      return <Badge>{t('Sold')}</Badge>;
    }

    if (isReserved) {
      return <Badge>{t('To be sold')}</Badge>;
    }

    if (isPriceReduced) {
      return <Badge>{t('Reduced price')}</Badge>;
    }
  }, [
    auction?.isSoldByBuyNow,
    auction?.soldFor,
    isComingSoon,
    isReserved,
    isPriceReduced,
    t
  ]);

  const breadCrumbsRef = useRef<HTMLDivElement>(null);

  const isBreadCrumbsInViewPort = useCallback(() => {
    const rect = breadCrumbsRef.current?.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    if (!rect) {
      return false;
    }

    return (
      rect.bottom >= 0 &&
      rect.top <= windowHeight &&
      rect.bottom <= windowHeight &&
      rect.top >= 0
    );
  }, [breadCrumbsRef]);

  const stickyCondition = useCallback(() => {
    if (!isBreadCrumbsInViewPort()) {
      return true; // When we have passed the breadcrumbs
    }
    return false;
  }, [isBreadCrumbsInViewPort]);

  const onSticky = useCallback(
    (isSticky: boolean) => {
      if (isSticky) {
        if (!isMobile) {
          dispatch(setHeaderHiddenAction(true));
          dispatch(setContactButtonIsHidden(false));
        }

        if (isMobile) {
          dispatch(setContactButtonIsHidden(false));
        }

        return;
      }

      if (!isMobile) {
        dispatch(setHeaderHiddenAction(false));
      }

      dispatch(setContactButtonIsHidden(true));
      dispatch(
        setContactButtonPosition(
          isMobile
            ? {
                bottom: '5.25rem'
              }
            : {}
        )
      );
    },
    [dispatch, isMobile]
  );

  useEffect(() => {
    // Close bidding view if auctionId changes
    if (auctionId !== biddingViewAuctionId) {
      dispatch(closeBiddingView());
    }
  }, [auctionId, biddingViewAuctionId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(closeBiddingView());
      dispatch(setContactButtonDefaultTriggerEnabled(true));
      dispatch(
        setContactButtonPosition({
          bottom: '2rem'
        })
      );
    };
  }, [dispatch]);

  if (redirectUri) {
    if (staticContext) {
      staticContext.statusCode = 301;
    }
    return <Redirect to={redirectUri} />;
  }

  if (isError404 || notEligibleForTargetedAuction) {
    if (staticContext) {
      staticContext.statusCode = 302;
    }

    return <Redirect to={translateRoute(t, '/ROUTES.FILTER_ALL_VEHICLES')} />;
  }

  if ((isFetching && !rawAuction) || isOldAuction || !auctionHasProcessObject) {
    return null;
  }

  if (
    auction?.auctionType &&
    location.pathname !== pathnameBasedOnAuctionType
  ) {
    return <Redirect to={pathnameBasedOnAuctionType} />;
  }

  return (
    <div>
      {!targetedAuctionGroupId && auctionId && (
        <Recombee auctionId={auctionId} />
      )}
      <BidOrReserveDrawer isOpen={isBiddingOpen} contentMaxWidth={1132}>
        <BiddingView />
      </BidOrReserveDrawer>
      <KvdHelperWrapper url={cmsData?.kvdHelperUrl ?? ''}>
        <Container>
          <Head
            {...headProps}
            meta={meta}
            link={[{ rel: 'canonical', href: canonicalUrl }]}
          />
          <Section maxWidth={CONTENT_WIDTH} spacing={0} backgroundColor="light">
            <BreadcrumbsWrapper ref={breadCrumbsRef} data-testid="breadcrumbs">
              <Button
                onClick={handleBack}
                variant="flat"
                color="info"
                colorShade="dark"
                prefixIcon={<BackIcon />}
              >
                {t('Back')}
              </Button>

              <div id="breadcrumbs-regular">
                <Breadcrumbs links={getBreadcrumbs(auction, t)} />
              </div>

              <div id="breadcrumbs-mobile">
                <BreadcrumbsDropdown {...getMobileBreadcrumb(auction, t)} />
              </div>
            </BreadcrumbsWrapper>
          </Section>
          <ResponsiveSection
            maxWidth={CONTENT_WIDTH}
            spacing={0}
            backgroundColor="light"
          >
            <GalleryWrapper>
              <Gallery
                images={media}
                auctionSlug={prettySlug(auction)}
                auction={auction}
                onImageClick={handleGalleryClick}
                isClickDisabled={isComingSoon}
              />
            </GalleryWrapper>
          </ResponsiveSection>
          <Section
            maxWidth={CONTENT_WIDTH}
            spacingTop={2}
            spacingBottom={0}
            shortDivider
            backgroundColor="light"
          >
            {renderedBadge && <BadgeContainer>{renderedBadge}</BadgeContainer>}
            <SummaryContainer>
              <Summary
                auction={auction}
                auctionStates={auctionStates}
                cmsData={objectConditionCmsData}
              />
            </SummaryContainer>
            {isElectrification && (
              <MajorFeatures
                isEVorPHEV={isEVorPHEV}
                processObject={auction?.processObject}
              />
            )}
          </Section>
          <ResponsiveSection
            id="buyMethodAnchor"
            maxWidth={CONTENT_WIDTH}
            spacing={0}
            backgroundColor="light"
            shortDivider
          >
            <BuyMethodContainer>
              {!isReserved && auction.auctionType === AuctionType.BIDDING && (
                <BuyMethodBox
                  auction={auction}
                  buyMethod={AuctionType.BIDDING}
                  auctionStates={auctionStates}
                  reservationPriceReached={reservationPriceReached}
                />
              )}
              {!isReserved &&
                auctionStates.isBuyNow &&
                !auction.isSoldByBuyNow &&
                !auctionStates.isClosed && (
                  <BuyMethodBox
                    auction={auction}
                    buyMethod={AuctionType.BUY_NOW}
                    auctionStates={auctionStates}
                    reservationPriceReached={reservationPriceReached}
                  />
                )}

              {(auction.isSoldByBuyNow ||
                auctionStates.isClosed ||
                isReserved) && (
                <SimilarCarsBox
                  hasSimilarObjects={Boolean(
                    rawAuction?.similarAuctions?.length
                  )}
                  auction={auction}
                />
              )}
            </BuyMethodContainer>
            <Spacer height={2} />
            <TrustPilotTop />
          </ResponsiveSection>
          <StickyOnScrollContainer
            stickyCondition={stickyCondition}
            isDisabled={isMobile && isBiddingOpen}
            onSticky={onSticky}
          >
            <StickyAuctionSummary
              isHeavyEquipment={isHeavyEquipment}
              picture={
                media.filter(image => {
                  return (
                    image.contentType === 'image' &&
                    image.type === 'PRESENTATION'
                  );
                })[0]
              }
              title={auction?.previewTitle ?? ''}
              carPackage={[auction?.previewPreamble, auction?.previewPackage]
                .filter(Boolean)
                .join(', ')}
            />
          </StickyOnScrollContainer>

          <AuctionDetails>
            {(showContactOnAuction || showContactOnFixedPrice) &&
              !auction.isSoldByBuyNow &&
              !auctionStates.isClosed && (
                <>
                  <IncludeContent to="tablet">
                    <Spacer height={2} />
                  </IncludeContent>

                  <Section
                    shortDivider
                    maxWidth={CONTENT_WIDTH}
                    backgroundColor="light"
                    spacing={0}
                  >
                    <Contact {...(contactSection as ContactSectionRecord)} />
                  </Section>
                </>
              )}
            <Spacer height={1.5} />
            {isHeavyEquipment && (
              <>
                <Section
                  maxWidth={CONTENT_WIDTH}
                  backgroundColor="light"
                  spacing={2}
                  divider="bottom"
                  shortDivider
                >
                  <BrieflyAboutObject auction={auction} />
                </Section>

                <Section
                  maxWidth={CONTENT_WIDTH}
                  backgroundColor="light"
                  spacing={2}
                >
                  <HeavyEquipmentDescription auction={auction} />
                </Section>
              </>
            )}

            {auction?.processObject?.valuation && !isHeavyEquipment && (
              <Section
                maxWidth={CONTENT_WIDTH}
                spacingTop={0}
                backgroundColor="light"
                shortDivider
              >
                {isComingSoon ? (
                  <div id="testAnchor">
                    <Title as="h3">{t('Result from KVD test pending')}</Title>
                    <SoonTestedExplanation>
                      {t(
                        "This vehicle is being tested, information about the test will show here once it's done"
                      )}
                    </SoonTestedExplanation>
                  </div>
                ) : (
                  <AuctionTest
                    processObject={auction.processObject}
                    media={media}
                    auctionStates={auctionStates}
                    showGrades={showGrades}
                    objectType={objectType}
                    title={auction.previewTitle}
                    isEVorPHEV={isEVorPHEV}
                  />
                )}
              </Section>
            )}
            <Section
              id="infoAnchor"
              maxWidth={CONTENT_WIDTH}
              backgroundColor="light"
              spacingBottom={2.5}
            >
              {(isComingSoon || hasEquipment || hasFacts) && (
                <div>
                  {isComingSoon && (
                    <EquipmentSoonAvailable>
                      {t(
                        'Information about the vehicle and technical specification is updated as we test the vehicle'
                      )}
                    </EquipmentSoonAvailable>
                  )}
                  {hasEquipment && (
                    <Equipment
                      processObject={auction.processObject}
                      isComingSoon={isComingSoon}
                    />
                  )}

                  <Spacer height={3} />

                  {hasFacts && (
                    <>
                      {isHeavyEquipment ? (
                        <HeavyEquipmentFacts
                          processObject={auction.processObject}
                        />
                      ) : (
                        <AuctionFacts
                          processObject={auction.processObject}
                          isComingSoon={isComingSoon}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </Section>
            {hasLocationData(auction?.processObject) && (
              <Section
                maxWidth={CONTENT_WIDTH}
                divider="top"
                shortDivider
                backgroundColor="light"
              >
                <ObjectLocation
                  processObject={auction?.processObject}
                  facility={facility}
                  cmsData={cmsData?.deliveryCalculator}
                  facilityExplanation={
                    cmsData?.heavyEquipment?.facilityExplanation
                  }
                />
              </Section>
            )}
            {isHeavyEquipment && cmsData?.heavyEquipment?.salesTerms && (
              <Section
                maxWidth={CONTENT_WIDTH}
                spacing={2.5}
                divider="top"
                shortDivider
                backgroundColor="light"
              >
                <HeavyEquipmentTerms />
              </Section>
            )}
            <Section
              maxWidth={CONTENT_WIDTH}
              spacing={2.5}
              divider="top"
              shortDivider
              backgroundColor="light"
            >
              <Social
                auction={auction}
                currentLocationPathName={location.pathname}
              />
            </Section>
          </AuctionDetails>
          {similarObjects.length > 0 && (
            <Section
              spacing={0}
              id="similar-auctions-section"
              backgroundColor="light"
            >
              <VehicleCardListBlockWrapper>
                <VehicleCardListBlock
                  headerProps={{
                    title: `${t('Recommended for you')}`
                  }}
                  auctions={similarObjects}
                  productCardVariant={ProductCardVariant.Regular}
                />
              </VehicleCardListBlockWrapper>
            </Section>
          )}
        </Container>
      </KvdHelperWrapper>
    </div>
  );
};

export const dataHook = async ({
  match: { params },
  dispatch,
  getState,
  t
}: DataHookFunctionProps<{ slug: string }>) => {
  let auction: Auction | AuctionNotFound;

  const auctionId = getIdFromSlug(params.slug);
  if (!auctionId) {
    return;
  }

  const storeObjects = getState().objectView?.storeObjects ?? {};
  const alreadyInStore = Object.keys(storeObjects).includes(auctionId);

  if (alreadyInStore) {
    auction = storeObjects[auctionId] as Auction;
  } else {
    const { payload } = await dispatch(getAuction(auctionId));
    auction = payload;
  }

  if (typeof auction === 'string') {
    return { isError404: true };
  }

  const brand = (auction as AuctionNotFound)?.brand;
  if (auction && brand) {
    const familyName = (auction as AuctionNotFound)?.familyName;
    const encodedBrand = encodeURIComponent(brand?.toLowerCase()?.trim());
    const encodedFamilyName = encodeURIComponent(
      familyName?.toLowerCase()?.trim()
    );

    const { currentLocale } = localizationSelector(getState());

    const { brandSlugs = [], familySlugs = [] } =
      (await getCmsData<
        AllBrandAndFamilySlugsQuery,
        CarAllBrandAndFamilySlugsData
      >({
        dispatch,
        getState,
        storeKey: 'carAllBrandAndFamilySlugs',
        query: AllBrandAndFamilySlugsDocument,
        respKeys: [{ parentKey: 'allCarBrandPages' }],
        options: { manyRecords: true },
        variables: { locale: currentLocale }
      })) ?? {};

    if (brandSlugs.includes(encodedBrand)) {
      const path = `${translateRoute(t, '/ROUTES.FILTER_CAR')}/${encodedBrand}`;
      if (familySlugs.includes(encodedFamilyName)) {
        return {
          redirectUri: `${path}/${encodedFamilyName}`
        };
      }
      return {
        redirectUri: path
      };
    }
    return { isError404: true };
  } else if (!auction) {
    return { isError404: true };
  }

  dispatch(addViewedAuction({ auction }));
  dispatch(setContactButtonDefaultTriggerEnabled(false)); // Block the contact button components default scroll trigger

  return {};
};

export default provideDataHook('AuctionHandler', dataHook)(AuctionHandler);
